import { Head } from '@inertiajs/react';

interface SeoHeadProps {
    title?: string;
    description?: string;
    keywords?: string;
    ogImage?: string;
    ogType?: string;
    canonical?: string;
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    jsonLd?: object;
}

export function SeoHead({ 
    title,
    description,
    keywords,
    ogImage,
    ogType = 'website',
    canonical,
    author,
    publishedTime,
    modifiedTime,
    jsonLd
}: SeoHeadProps) {
    const appName = import.meta.env.VITE_APP_NAME || 'Brajti';
    // Don't append app name here since Inertia already does it in app.tsx
    const pageTitle = title || appName;
    // For meta tags, we want the full title with app name
    const fullTitle = title ? `${title} - ${appName}` : appName;
    
    return (
        <Head>
            <title>{pageTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            
            {/* Open Graph */}
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={ogType} />
            {ogImage && <meta property="og:image" content={ogImage} />}
            {canonical && <meta property="og:url" content={canonical} />}
            
            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            {ogImage && <meta name="twitter:image" content={ogImage} />}
            
            {/* Additional SEO */}
            {canonical && <link rel="canonical" href={canonical} />}
            {author && <meta name="author" content={author} />}
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
            
            {/* JSON-LD Structured Data */}
            {jsonLd && (
                <script 
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
                />
            )}
        </Head>
    );
}